/**
 * Handles horizontal panning of the week timeline via mouse drag gestures.
 *
 * Drag-scroll is explicitly opt-in through `enabled` and is disabled for
 * interactive descendants so native element interactions keep working.
 */
export class TimelineWeekDragScroll {
	// Applied while drag-scroll capability is available for the current viewport.
	private static readonly DRAG_SCROLL_ENABLED_CLASS = "timeline-drag-scroll-enabled";

	// Applied only during an active drag-scroll gesture.
	private static readonly DRAG_SCROLLING_CLASS = "timeline-drag-scrolling";

	// Small movement threshold to separate clicks from intentional drag gestures.
	private static readonly DRAG_SCROLL_START_THRESHOLD_PX = 4;

	// Descendants that should keep their native pointer behavior (click, focus, context actions).
	private static readonly DRAG_SCROLL_INTERACTIVE_SELECTOR = [
		"a",
		"button",
		"input",
		"select",
		"textarea",
		"label",
		"[role='button']",
		"[role='link']",
		"[data-toggle='context']",
		"[data-context-menu-container]",
		"[data-context-menu-button]",
		".dropdown-menu",
		".day-based-working-time",
		".item-none"
	].join(", ");

	// Whether drag-scroll interaction is currently active — true only when the container has horizontal overflow.
	private enabled = false;
	private dragScrollState: { startClientX: number; startScrollLeft: number; isDragScrollActive: boolean } | null = null;

	constructor(
		private readonly host: HTMLElement,
		private readonly container: HTMLElement
	) {}

	/**
	 * Wires the initial pointer entrypoint on the scroll container.
	 */
	public attach(): void {
		this.container.addEventListener("mousedown", this.startDragScroll);
	}

	/**
	 * Removes listeners and clears in-flight drag state.
	 */
	public detach(): void {
		this.container.removeEventListener("mousedown", this.startDragScroll);
		this.stopDragScroll();
	}

	/**
	 * Enables/disables drag-scroll interaction and corresponding host CSS marker.
	 * Disabling immediately aborts any active gesture.
	 */
	public setEnabled(enabled: boolean): void {
		this.enabled = enabled;
		this.host.classList.toggle(TimelineWeekDragScroll.DRAG_SCROLL_ENABLED_CLASS, enabled);
		if (!enabled) {
			this.stopDragScroll();
		}
	}

	/**
	 * Entry point for a drag-scroll gesture, triggered on mousedown.
	 * Captures the initial pointer and scroll position as anchors for the subsequent movement tracking in `updateDragScroll(...)`.
	 */
	private readonly startDragScroll = (event: MouseEvent): void => {
		// Only start when drag-scroll is enabled and the primary (left) mouse button is pressed.
		if (!this.enabled || event.button !== 0) {
			return;
		}

		// Skip interactive descendants (links, buttons, inputs, etc.) so their native behavior is preserved.
		if (!(event.target instanceof HTMLElement) || event.target.closest(TimelineWeekDragScroll.DRAG_SCROLL_INTERACTIVE_SELECTOR)) {
			return;
		}

		this.dragScrollState = {
			startClientX: event.clientX, // anchor for deltaX calculation while active scrolling
			startScrollLeft: this.container.scrollLeft, // anchor for scroll position calculation while active scrolling
			isDragScrollActive: false // stays false until the movement threshold is exceeded in updateDragScroll
		};

		// Suppress text selection from the moment the mouse button is pressed, not just after the movement threshold
		// is exceeded. Without this, dragging into the sidebar selects text across unrelated page elements.
		document.body.style.userSelect = "none";

		// Register on document instead of the container so the gesture keeps tracking when the pointer leaves the container bounds.
		document.addEventListener("mousemove", this.updateDragScroll);
		document.addEventListener("mouseup", this.stopDragScroll);
	};

	/**
	 * Tracks pointer movement during an active drag gesture and scrolls the container accordingly.
	 * Scrolling begins only after the movement threshold is exceeded to avoid misclassifying clicks as drags.
	 */
	private readonly updateDragScroll = (event: MouseEvent): void => {
		// mousemove is registered on document, so it may fire even without an active drag gesture.
		if (!this.dragScrollState) {
			return;
		}

		// Calculate horizontal distance moved since drag started
		const deltaX = event.clientX - this.dragScrollState.startClientX;

		// Wait until movement passes a small threshold so click interactions are not misclassified as drag.
		if (!this.dragScrollState.isDragScrollActive && Math.abs(deltaX) < TimelineWeekDragScroll.DRAG_SCROLL_START_THRESHOLD_PX) {
			return;
		}

		// Transition from "potential drag" to active drag exactly once after threshold is exceeded.
		if (!this.dragScrollState.isDragScrollActive) {
			this.dragScrollState.isDragScrollActive = true;
			this.host.classList.add(TimelineWeekDragScroll.DRAG_SCROLLING_CLASS);
		}

		// Suppress native selection/default drag behavior so pointer movement exclusively drives timeline panning (to avoid text-marking, native drag, etc).
		event.preventDefault();

		// Subtract deltaX so dragging left increases scrollLeft — the content moves with the pointer, not against it.
		this.container.scrollLeft = this.dragScrollState.startScrollLeft - deltaX;
	};

	/**
	 * Finishes the current drag gesture and removes transient document listeners/state.
	 */
	private readonly stopDragScroll = (): void => {
		// Cleanup to avoid stale listeners/CSS/state between interactions.
		document.removeEventListener("mousemove", this.updateDragScroll);
		document.removeEventListener("mouseup", this.stopDragScroll);
		this.host.classList.remove(TimelineWeekDragScroll.DRAG_SCROLLING_CLASS);
		document.body.style.userSelect = "";
		this.dragScrollState = null;
	};
}
